import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, img, useApi, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SingleImage } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BASE_CATS = ["Food & Drink", "Horse Tours & Equestrian", "Pickleball & Sports", "Services", "Wellness"];
const NEW_CAT = "__new__";

const CategoryField = ({ value, onChange, categories }) => {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="font-bold">Category</Label>
      {adding ? (
        <div className="flex gap-2">
          <Input autoFocus placeholder="New category name" value={value} onChange={(e) => onChange(e.target.value)} data-testid="directory-field-category-new" />
          <button type="button" onClick={() => setAdding(false)} className="btn-outline !px-3 !py-2" data-testid="directory-category-cancel-new">List</button>
        </div>
      ) : (
        <Select value={value || undefined} onValueChange={(v) => (v === NEW_CAT ? (setAdding(true), onChange("")) : onChange(v))}>
          <SelectTrigger data-testid="directory-field-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c} value={c} data-testid={`directory-category-option-${c}`}>{c}</SelectItem>)}
            <SelectItem value={NEW_CAT} data-testid="directory-category-option-new">+ Add new category…</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

const ES_FIELDS = [
  { key: "name", label: "Nombre", type: "input" }, { key: "category", label: "Categoría", type: "input" },
  { key: "description", label: "Descripción", rows: 3 }, { key: "cta_label", label: "Texto del botón", type: "input" },
];

const BLANK = { name: "", category: "", description: "", image: "", website_url: "", cta_label: "", cta_url: "", order: 0, published: true, es: {} };
const FIELDS = [
  ["name", "Business name"], ["cta_label", "Button label"],
  ["cta_url", "Button link (WhatsApp / booking)"], ["website_url", "Website / social link"], ["order", "Sort order"],
];

const EntryForm = ({ entry, onSaved, categories }) => {
  const [f, setF] = useState(entry);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, order: Number(f.order) || 0, es: cleanEs(f.es) };
      if (f.id) await api.put(`/admin/directory/${f.id}`, body);
      else await api.post("/admin/directory", body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="directory-form">
      <SingleImage value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="directory-image-upload" />
      <div className="grid gap-4 sm:grid-cols-2">
        <CategoryField value={f.category} onChange={(v) => setF({ ...f, category: v })} categories={categories} />
        {FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label className="font-bold">{label}</Label>
            <Input required={k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`directory-field-${k}`} />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label className="font-bold">Description</Label>
        <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="directory-field-description" />
      </div>
      <label className="flex items-center gap-3 font-bold"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="directory-field-published" /> Published</label>
      <SpanishFields fields={ES_FIELDS} source={f} value={f.es || {}} onChange={(es) => setF({ ...f, es })} testId="directory-es" />
      <button type="submit" className="btn-primary w-full" data-testid="directory-save">Save entry</button>
    </form>
  );
};

export default function AdminDirectory() {
  const { data, reload } = useApi("/admin/directory", []);
  const [editing, setEditing] = useState(null);
  const categories = [...new Set([...BASE_CATS, ...data.map((d) => d.category).filter(Boolean)])].sort();
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    await api.delete(`/admin/directory/${d.id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div data-testid="admin-directory">
      <AdminHeader title="Directory">
        <button onClick={() => setEditing(BLANK)} className="btn-primary" data-testid="directory-new"><Plus className="h-4 w-4" /> New entry</button>
      </AdminHeader>
      <div className="overflow-hidden rounded-2xl border border-clay bg-white">
        {data.map((d) => (
          <div key={d.id} className="flex items-center gap-4 border-b border-clay p-4 last:border-b-0" data-testid={`admin-directory-row-${d.id}`}>
            <img src={img(d.image)} alt="" className="h-16 w-20 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-ink">{d.name} {!d.published && <span className="text-xs text-moss">(hidden)</span>}</p>
              <p className="text-sm font-semibold text-sage">{d.category}</p>
            </div>
            <button onClick={() => setEditing(d)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="Edit" data-testid={`directory-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(d)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-destructive" aria-label="Delete" data-testid={`directory-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-forest">{editing?.id ? "Edit entry" : "New entry"}</DialogTitle></DialogHeader>
          {editing && <EntryForm entry={editing} categories={categories} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
